import { useEffect, useMemo, useRef, useState } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";
import { mapStyles } from "../../../../../utils/map-styles.js";
import { useSpiderfy } from "../../../../../components/map-report/spiderfy-marker.jsx";

const API_URL = import.meta.env.VITE_API_URL;
const position = { lat: -23.0, lng: -46.0 };

const MapContent = ({ data = [], controller, sync = false, mapState, isPrinting }) => {
    const map = useMap();

    const overlayRef = useRef(null);
    const markersRef = useRef([]);
    const originalPosRef = useRef(new Map());

    const isFittingRef = useRef(false);
    const isInternalUpdateRef = useRef(false);
    const syncTimeoutRef = useRef(null);

    const { spiderfy, unspiderfy, spiderfiedRef } = useSpiderfy(map);

    const viewState = controller.viewState;
    const setViewState = controller.setViewState;

    const [heatmapImage, setHeatmapImage] = useState(null);
    const printOverlayRef = useRef(null);
    const printStageRef = useRef("idle");

    const points = useMemo(() => {
        return data
            .map(item => {
                if (!item.system_latitude || !item.system_longitude) return null;

                const lat = Number(item.system_latitude ?? position.lat);
                const lng = Number(item.system_longitude ?? position.lng);

                if (isNaN(lat) || isNaN(lng)) return null;

                return { lat, lng };
            })
            .filter(Boolean);
    }, [data]);

    useEffect(() => {
        if (!map) return;

        map.setOptions({
            styles: mapStyles({
                neighborhoodStreets: mapState.neighborhoodStreets,
                pointsOfInterest: mapState.pointsOfInterest
            })
        });
    }, [map, mapState]);

    useEffect(() => {
        if (!map) return;

        if (!overlayRef.current) {
            overlayRef.current = new GoogleMapsOverlay({});
            overlayRef.current.setMap(map);
        }

        return () => {
            overlayRef.current?.setMap(null);
        };
    }, [map]);

    useEffect(() => {
        if (!map || !isPrinting) return;

        setHeatmapImage(null);
        let frameId;

        const capture = () => {
            const canvas =
                map.getDiv()?.querySelector(".deckgl-overlay canvas") ||
                map.getDiv()?.querySelector("canvas");

            if (!canvas) {
                frameId = requestAnimationFrame(capture);
                return;
            }

            try {
                const img = canvas.toDataURL("image/png");
                setHeatmapImage(img);
            } catch (err) {
                console.warn("Falha ao capturar heatmap:", err);
            }
        };

        frameId = requestAnimationFrame(capture);

        return () => cancelAnimationFrame(frameId);
    }, [map, isPrinting, controller.mapMode]);

    useEffect(() => {
        if (!map || points.length === 0) return;

        const bounds = new google.maps.LatLngBounds();
        points.forEach(p => {bounds.extend(new google.maps.LatLng(p.lat, p.lng));});

        isFittingRef.current = true;
        isInternalUpdateRef.current = true;

        if (!bounds.isEmpty()) map.fitBounds(bounds, 40);

        const t = setTimeout(() => {
            isFittingRef.current = false;
            isInternalUpdateRef.current = false;
        }, 250);

        return () => clearTimeout(t);

    }, [points, map]);

    useEffect(() => {
        if (!map || !sync || !viewState) return;
        if (isFittingRef.current) return;

        isInternalUpdateRef.current = true;

        map.setCenter(viewState.center);
        map.setZoom(viewState.zoom);

        const t = setTimeout(() => {
            isInternalUpdateRef.current = false;
        }, 80);

        return () => clearTimeout(t);

    }, [viewState, map, sync]);

    useEffect(() => {
        if (!map || !sync) return;

        const listener = map.addListener("idle", () => {
            if (isFittingRef.current) return;
            if (isInternalUpdateRef.current) return;

            if (syncTimeoutRef.current) {
                clearTimeout(syncTimeoutRef.current);
            }

            syncTimeoutRef.current = setTimeout(() => {
                const center = map.getCenter();
                const zoom = map.getZoom();

                setViewState({
                    center: {
                        lat: center.lat(),
                        lng: center.lng()
                    },
                    zoom
                });
            }, 120);

        });

        return () => {
            listener.remove();
            if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
        };

    }, [map, sync]);

    useEffect(() => {
        if (!map) return;

        if (!isPrinting) {
            printStageRef.current = "idle";
            return;
        }

        printStageRef.current = "preparing";

        const t = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                printStageRef.current = "ready";
            });
        });

        return () => cancelAnimationFrame(t);

    }, [isPrinting, controller.mapMode, map]);

    useEffect(() => {
        if (!map) return;

        if (spiderfiedRef.current) {
            unspiderfy(markersRef, originalPosRef);
        }

        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];
        originalPosRef.current.clear();

        if (overlayRef.current) {
            overlayRef.current.setProps({ layers: [] });
        }

        if (printOverlayRef.current) {
            printOverlayRef.current.setMap(null);
            printOverlayRef.current = null;
            setHeatmapImage(null);
        }

        if (controller.mapMode === "heatmap") {

            if (isPrinting && heatmapImage && !printOverlayRef.current && printStageRef.current === "ready") {
                const imgOverlay = new google.maps.OverlayView();

                imgOverlay.onAdd = function () {
                    const div = document.createElement("div");

                    div.style.position = "absolute";
                    div.style.top = "0";
                    div.style.left = "0";
                    div.style.width = "100%";
                    div.style.height = "100%";
                    div.style.pointerEvents = "none";

                    const img = document.createElement("img");
                    img.src = heatmapImage;

                    img.style.position = "absolute";
                    img.style.top = "0";
                    img.style.left = "0";
                    img.style.width = "100%";
                    img.style.height = "100%";
                    img.style.objectFit = "cover";

                    div.appendChild(img);
                    this.div = div;

                    this.getPanes().overlayLayer.appendChild(div);
                };

                imgOverlay.draw = function () {
                    const projection = this.getProjection();
                    if (!projection || !this.div) return;

                    const bounds = map.getBounds();
                    if (!bounds) return;

                    const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
                    const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());

                    this.div.style.left = sw.x + "px";
                    this.div.style.top = ne.y + "px";
                    this.div.style.width = (ne.x - sw.x) + "px";
                    this.div.style.height = (sw.y - ne.y) + "px";
                };

                imgOverlay.onRemove = function () {
                    if (this.div?.parentNode) {
                        this.div.parentNode.removeChild(this.div);
                    }

                    this.div = null;
                };

                imgOverlay.setMap(map);
                printOverlayRef.current = imgOverlay;
                return;
            }

            overlayRef.current?.setProps({
                layers: [
                    new HeatmapLayer({
                        id: "heatmap",
                        data: points,

                        getPosition: d => [d.lng, d.lat],

                        radiusPixels: controller.radius,
                        intensity: 0.8,
                        threshold: 0.03
                    })
                ]
            });

            return;
        }

        if (controller.mapMode === "marker") {

            points.forEach((p, index) => {
                const marker = new google.maps.Marker({
                    position: { lat: p.lat, lng: p.lng },
                    map,
                    icon: {
                        url: `${API_URL}/public/assets/img/markers/default.svg`,
                        scaledSize: new google.maps.Size(28, 28)
                    }
                });

                originalPosRef.current.set(index, marker.getPosition());
                markersRef.current.push(marker);

                marker.addListener("click", () => {
                    const projection = map.getProjection();
                    if (!projection) return;

                    const group = markersRef.current.filter(m => {
                        const p1 = projection.fromLatLngToPoint(m.getPosition());
                        const p2 = projection.fromLatLngToPoint(marker.getPosition());

                        const scale = Math.pow(2, map.getZoom());
                        const dx = (p1.x - p2.x) * scale;
                        const dy = (p1.y - p2.y) * scale;

                        return Math.sqrt(dx * dx + dy * dy) < 20;
                    });

                    if (group.length > 1 && !spiderfiedRef.current) {
                        spiderfy(group);
                    }
                });
            });

            const mapClick = map.addListener("click", () => {
                if (spiderfiedRef.current) {
                    unspiderfy(markersRef, originalPosRef);
                }
            });

            return () => mapClick.remove();
        }

    }, [points, controller.mapMode, controller.radius, map, heatmapImage, isPrinting]);

    return null;
};

export default MapContent;
